import SheetLink from "@/components/widgets/partials/SheetLink";
import { getMCPMonitorTopSources } from "@/api/endpoints/searches";
import { SENTIMENT, Source, SOURCE_CODE_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { MCPMonitorTopSourcesWithDetails } from "@/types/Search";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { Avatar, AvatarFallback, AvatarImage } from "@components/ui/avatar";
import { Badge } from "@components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/ui/table";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetTitle,
} from "@components/ui/widget";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "@components/widgets/utils";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { SENTIMENTS_BY_GROUP } from "@/utils/sentiment";

export default function TopSourcesPositiveWidget({
  searchUuid,
  filters,
  ...rest
}: WidgetProps & {
  filters: SearchFilterV4WithDates;
  searchUuid: Uuid;
}) {
  // Server state
  const { data: topSourcesData, isLoading: isTopSourcesLoading } = useQuery({
    queryKey: [
      searchUuid,
      "insights-sentiment",
      "top-sources-positive",
      filters,
    ],
    queryFn: async () => {
      return getMCPMonitorTopSources(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        sentiment: SENTIMENTS_BY_GROUP[SENTIMENT.POSITIVE],
        metric: "reach",
        details: true,
      });
    },
  });

  return (
    <Widget {...rest}>
      <WidgetHeader isLoading={isTopSourcesLoading} skeletonClassName="h-11.5">
        <div className="flex flex-col gap-0.5">
          <WidgetTitle>
            <Trans>Källor som driver positivt sentiment</Trans>
          </WidgetTitle>

          <p className="text-muted-foreground text-sm">
            <Trans>Rankade efter räckvidd</Trans>
          </p>
        </div>
      </WidgetHeader>
      <WidgetContent
        className="overflow-y-auto **:data-[slot=table-container]:overflow-visible"
        isEmpty={!isTopSourcesLoading && !topSourcesData?.ok}
        isLoading={isTopSourcesLoading}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Trans>Källa</Trans>
              </TableHead>

              <TableHead>
                <Trans>Räckvidd</Trans>
              </TableHead>

              <TableHead>
                <Trans>Sentiment</Trans>
              </TableHead>

              <TableHead>
                <Trans>Träffar</Trans>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {topSourcesData?.ok && topSourcesData.data.sources.length ? (
              topSourcesData.data.sources.map((source) => (
                <SourceRow key={source.channel_id} source={source} />
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="h-24 text-center text-muted-foreground"
                >
                  <Trans>Inga kanalar hittade</Trans>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </WidgetContent>
    </Widget>
  );
}

function SourceRow({
  source,
}: {
  source: MCPMonitorTopSourcesWithDetails["sources"][0];
}) {
  const { t } = useLingui();
  const { abbreviateNumber } = useI18n();

  // Helpers
  const channelName = source.channel_name || t`Okänd källa`;

  return (
    <TableRow key={source.channel_id}>
      <TableCell className="flex items-center gap-2">
        <Avatar>
          <AvatarImage
            loading="eager"
            src={source.channel_artwork_url || undefined}
            alt=""
          />

          <AvatarFallback>{channelName.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>

        <div className="flex flex-col gap-px">
          {source.channel_name ? (
            <SheetLink
              overrides={{
                channel_name: source.channel_name,
                sentiment: SENTIMENTS_BY_GROUP[SENTIMENT.POSITIVE],
              }}
              widget="top_sources_positive"
              action="channel"
              className="font-medium max-w-56 truncate"
            >
              {channelName}
            </SheetLink>
          ) : (
            <span className="font-medium max-w-56 truncate">{channelName}</span>
          )}

          <span className="text-xs text-muted-foreground">
            {i18n._(SOURCE_CODE_TO_LABEL[source.channel_type as Source])}
          </span>
        </div>
      </TableCell>

      <TableCell>{abbreviateNumber(source.value, 0)}</TableCell>

      <TableCell>
        <Badge variant="green" intensity="strong">
          <Trans>Positiv</Trans>
        </Badge>
      </TableCell>

      <TableCell>{abbreviateNumber(source.episode_count)}</TableCell>
    </TableRow>
  );
}
